import { InfoSet } from "../InfoSets/InfoSet";
import { Device } from "../Core/Device";
import { MacAddress } from "../Core/MacAddress";
import { List } from "../Utils/List";
import { HashSet } from "../Utils/HashSet";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";


export class Win32_NetworkAdapterTable extends InfoSet
{
	private _netAdapterTable: List<Win32_NetworkAdapterEntry>;

	constructor(device: Device) {
		super(device)

		this._netAdapterTable = new List<Win32_NetworkAdapterEntry>();
	}

	public Add(adapterEntry: Win32_NetworkAdapterEntry): boolean {
		if (adapterEntry == null)
			return false;

		let newEntry: Win32_NetworkAdapterEntry = null;

		if (adapterEntry.HasDeviceID)
			newEntry = this.FindByDeviceID(adapterEntry.DeviceID);

		if (newEntry == null) {
			if (adapterEntry.HasName)
				newEntry = this.FindByName(adapterEntry.Name);
		}

		if (newEntry == null) {
			this._netAdapterTable.Add(adapterEntry);
			return true;
		}

		return false;
	}

	public Clear(): void {
		this._netAdapterTable.Clear();
	}

	public get Count(): number 	{
		return this._netAdapterTable.Count;
	}

	public get HasMacAddresses(): boolean {
		return true;
	}

	public get MacAddresses(): HashSet<MacAddress> {
		let macAddresses: HashSet<MacAddress> = new HashSet<MacAddress>();

		this._netAdapterTable.ForEach((adapterEntry: Win32_NetworkAdapterEntry) => {
			if (!adapterEntry.HasMacAddress)
				return;
			if (!adapterEntry.MacAddress.IsValidAddress)
				return;

			macAddresses.Add(adapterEntry.MacAddress);
		}, this);

		return (macAddresses);
	}

	public FindByDeviceID(deviceID: string): Win32_NetworkAdapterEntry {

		let foundEntry: Win32_NetworkAdapterEntry = null;

		for (let i = 0; i < this._netAdapterTable.Count; ++i) {

			let entry: Win32_NetworkAdapterEntry = this._netAdapterTable.Get(i);

			if (!entry.HasDeviceID)
				continue;
			if (entry.DeviceID != deviceID)
				continue;

			foundEntry = entry;
			break;
		}

		return (foundEntry);
	}

	public FindByName(name: string): Win32_NetworkAdapterEntry {

		let foundEntry: Win32_NetworkAdapterEntry = null;

		for (let i = 0; i < this._netAdapterTable.Count; ++i) {

			let entry: Win32_NetworkAdapterEntry = this._netAdapterTable.Get(i);

			if (!entry.HasName)
				continue;
			if (entry.Name != name)
				continue;

			foundEntry = entry;
			break;
		}

		return (foundEntry);
	}

	public Remove(adapterEntry: Win32_NetworkAdapterEntry): void {
		this._netAdapterTable.Remove(adapterEntry);
	}

	public ForEach(callbackfn: (value: Win32_NetworkAdapterEntry, index: number, array: Win32_NetworkAdapterEntry[]) => void, thisArg?: any): void {
		this._netAdapterTable.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: Win32_NetworkAdapterEntry, index: number, array: Win32_NetworkAdapterEntry[]) => boolean, thisArg?: any): boolean {
		return this._netAdapterTable.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: Win32_NetworkAdapterEntry, index: number, array: Win32_NetworkAdapterEntry[]) => boolean, thisArg?: any): boolean {
		return this._netAdapterTable.Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		if (this._netAdapterTable.Count > 0) {
			obj.w32_net_ad_info = {};
			obj = obj.w32_net_ad_info;

			obj.entries = [];
			ObjSerUtils.WriteObjSerCollection(obj.entries, this._netAdapterTable, context);
		}
	}

	public Read(obj: any, context: any): void {
		if (!obj.w32_net_ad_info)
			throw new Error();
		obj = obj.w32_net_ad_info;

		this.Clear();

		if (obj.entries !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.entries, this._netAdapterTable, (value: any) => {
				return new Win32_NetworkAdapterEntry();
			}, context);
		}
	}

}


export class Win32_NetworkAdapterEntry {

	public AdapterType: string;
	public get HasAdapterType(): boolean {
		return !!this.AdapterType;
	}

	public Availability: number;
	public HasAvailability(): boolean {
		if (this.Availability >= 0)
			return true;
		return false;
	}

	public AvailabilityToString(): string {

		let valueStr = "Unknown";

		switch (this.Availability) {
			case 1: valueStr = "Other"; break;
			case 2: valueStr = "Unknown"; break;
			case 3: valueStr = "Running"; break;
			case 4: valueStr = "Warning"; break;
			case 5: valueStr = "In Test"; break;
			case 6: valueStr = "Not Applicable"; break;
			case 7: valueStr = "Power Off"; break;
			case 8: valueStr = "Off Line"; break;
			case 9: valueStr = "Off Duty"; break;
			case 10: valueStr = "Degraded"; break;
			case 11: valueStr = "Not Installed"; break;
			case 12: valueStr = "Install Error"; break;
			case 13: valueStr = "Power Save - Unknown"; break;
			case 14: valueStr = "Power Save - Low Power"; break;
			case 15: valueStr = "Power Save - Standby"; break;
			case 16: valueStr = "Power Cycle"; break;
			case 17: valueStr = "Power Save = Warning"; break;
		}

		return (valueStr);
	}

	public Caption: string;
	public get HasCaption(): boolean {
		return !!this.Caption;
	}

	public Description: string;
	public get HasDescription(): boolean {
		return !!this.Description;
	}

	public DeviceID: string;
	public get HasDeviceID(): boolean {
		return !!this.DeviceID;
	}

	public Index: number;
	public get HasIndex(): boolean {
		if (this.Index >= 0)
			return true;
		return false;
	}

	public InstallDate: number;
	public get HasInstallDate(): boolean {
		return (this.InstallDate >= 0);
	}

	public InterfaceIndex: number;
	public get HasInterfaceIndex(): boolean {
		if (this.InterfaceIndex >= 0)
			return true;
		return false;
	}

	public MacAddress: MacAddress;
	public get HasMacAddress(): boolean {
		if (!this.MacAddress)
			return false;
		if (this.MacAddress.IsNull)
			return false;
		return true;
	}

	public Manufacturer: string;
	public get HasManufacturer(): boolean {
		return !!this.Manufacturer;
	}

	public MaxSpeed: number;
	public get HasMaxSpeed(): boolean {
		if (this.MaxSpeed > 0)
			return true;
		return false;
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public NetEnabled: boolean;
	public get IsNetEnabled(): boolean {
		return this.NetEnabled;
	}

	public PhysicalAdapter: boolean;
	public get IsPhysicalAdapter(): boolean {
		return this.PhysicalAdapter;
	}

	public ProductName: string;
	public get HasProductName(): boolean {
		return !!this.ProductName;
	}

	public Speed: number;
	public get HasSpeed(): boolean {
		if (this.Speed > 0)
			return true;
		return false;
	}

	public Status: string;
	public get HasStatus(): boolean {
		return !!this.Status;
	}

	constructor() {
		this.Init();
	}

	private Init(): void {
		this.AdapterType = "";
		this.Availability = -1;
		this.Caption = "";
		this.Description = "";
		this.DeviceID = "";
		this.Index = -1;
		this.InstallDate = -1;
		this.InterfaceIndex = -1;
		this.MacAddress = null;
		this.Manufacturer = "";
		this.MaxSpeed = 0;
		this.Name = "";
		this.NetEnabled = false;
		this.PhysicalAdapter = false;
		this.ProductName = "";
		this.Speed = 0;
		this.Status = "";
	}

	public Write(obj: any, context: any): void {
		obj.win32_network_adapter_entry = {};
		obj = obj.win32_network_adapter_entry;

		if (this.HasAdapterType)
			obj.type = this.AdapterType;
		if (this.HasAvailability)
			obj.availability = ValueEncoder.EncodeInteger(this.Availability);
		if (this.HasCaption)
			obj.caption = this.Caption;
		if (this.HasDescription)
			obj.description = this.Description;
		if (this.HasDeviceID)
			obj.device_id = this.DeviceID;
		if (this.HasIndex)
			obj.index = ValueEncoder.EncodeInteger(this.Index);
		if (this.HasInstallDate)
			obj.install_date = ValueEncoder.EncodeInteger(this.InstallDate);
		if (this.HasInterfaceIndex)
			obj.if_index = ValueEncoder.EncodeInteger(this.InterfaceIndex);
		if (this.HasMacAddress)
			obj.mac_address = ValueEncoder.EncodeMacAddress(this.MacAddress);
		if (this.HasManufacturer)
			obj.manufacturer = this.Manufacturer;
		if (this.HasMaxSpeed)
			obj.max_speed = ValueEncoder.EncodeInteger(this.MaxSpeed);
		if (this.HasName)
			obj.name = this.Name;
		obj.net_enabled = ValueEncoder.EncodeBoolean(this.NetEnabled);
		obj.physical_adapter = ValueEncoder.EncodeBoolean(this.PhysicalAdapter);
		if (this.HasProductName)
			obj.product_name = this.ProductName;
		if (this.HasSpeed)
			obj.speed = ValueEncoder.EncodeInteger(this.Speed);
		if (this.HasStatus)
			obj.status = this.Status;
	}

	public Read(obj: any, context: any): void {
		if (!obj.win32_network_adapter_entry)
			throw new Error();
		obj = obj.win32_network_adapter_entry;

		if (obj.type !== undefined)
			this.AdapterType = obj.type;
		if (obj.availability !== undefined)
			this.Availability = ValueEncoder.DecodeInteger(obj.availability);
		if (obj.caption !== undefined)
			this.Caption = obj.caption;
		if (obj.description !== undefined)
			this.Description = obj.description;
		if (obj.device_id !== undefined)
			this.DeviceID = obj.device_id;
		if (obj.index !== undefined)
			this.Index = ValueEncoder.DecodeInteger(obj.index);
		if (obj.install_date !== undefined)
			this.InstallDate = ValueEncoder.DecodeInteger(obj.install_date);
		if (obj.if_index !== undefined)
			this.InterfaceIndex = ValueEncoder.DecodeInteger(obj.if_index);
		if (obj.mac_address !== undefined)
			this.MacAddress = ValueEncoder.DecodeMacAddress(obj.mac_address);
		if (obj.manufacturer !== undefined)
			this.Manufacturer = obj.manufacturer;
		if (obj.max_speed !== undefined)
			this.MaxSpeed = ValueEncoder.DecodeInteger(obj.max_speed);
		if (obj.name !== undefined)
			this.Name = obj.name;
		if (obj.net_enabled !== undefined)
			this.NetEnabled = ValueEncoder.DecodeBoolean(obj.net_enabled);
		if (obj.physical_adapter !== undefined)
			this.PhysicalAdapter = ValueEncoder.DecodeBoolean(obj.physical_adapter);
		if (obj.product_name !== undefined)
			this.ProductName = obj.product_name;
		if (obj.speed !== undefined)
			this.Speed = ValueEncoder.DecodeInteger(obj.speed);
		if (obj.status !== undefined)
			this.Status = obj.status;
	}

}